import React from 'react';
import { Box, Typography, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces';
import type { TransformedElement } from '@/app/queries/evaluationProtocolQueries';
import ElementCheckbox from './ElementCheckbox';

interface CategorySectionProps {
  categoryId: number;
  title: string;
  elements: TransformedElement[];
  selectedElementsSet: Set<number>;
  onCategorySelectAllToggle: (elements: TransformedElement[], isCurrentlyAllSelected: boolean) => void;
  onElementToggle: (element: TransformedElement) => void;
  showDivider: boolean;
}

const CategorySection: React.FC<CategorySectionProps> = React.memo(({
  categoryId,
  title,
  elements,
  selectedElementsSet,
  onCategorySelectAllToggle,
  onElementToggle,
  showDivider
}) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);

  const isAllSelected = React.useMemo(() => {
    return elements.length > 0 && elements.every(element => selectedElementsSet.has(element.id));
  }, [elements, selectedElementsSet]);

  const isSomeSelected = React.useMemo(() => {
    return elements.some(element => selectedElementsSet.has(element.id));
  }, [elements, selectedElementsSet]);

  const selectedCount = React.useMemo(() => {
    return elements.filter(element => selectedElementsSet.has(element.id)).length;
  }, [elements, selectedElementsSet]);

  const handleSelectAllToggle = React.useCallback(() => {
    onCategorySelectAllToggle(elements, isAllSelected);
  }, [elements, isAllSelected, onCategorySelectAllToggle]);

  if (!elements || elements.length === 0) {
    return null;
  }

  return (
    <Box key={categoryId} sx={{ mb: 3 }}>
      {/* Category Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              backgroundColor: selectedCount > 0 ? 'primary.50' : 'grey.100',
              px: 1,
              py: 0.25,
              borderRadius: 1,
            }}
          >
            {selectedCount}/{elements.length}
          </Typography>
        </Box>
        
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isAllSelected}
              indeterminate={isSomeSelected && !isAllSelected}
              onChange={handleSelectAllToggle}
              sx={{ 
                color: 'primary.main',
                '&.Mui-checked': { color: 'primary.main' },
                '&.MuiCheckbox-indeterminate': { color: 'primary.main' }
              }}
            />
          }
          label={
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 500,
                color: 'text.secondary',
              }}
            >
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.selectAll))}
            </Typography>
          }
          sx={{ 
            m: 0,
            '& .MuiFormControlLabel-label': {
              ml: 0.5
            }
          }}
        />
      </Box>
      
      {/* Elements */}
      <FormGroup sx={{ gap: 1.5 }}>
        {elements.map((element) => (
          <ElementCheckbox
            key={element.id}
            element={element}
            isSelected={selectedElementsSet.has(element.id)}
            onToggle={onElementToggle}
          />
        ))}
      </FormGroup>
    </Box>
  );
});

CategorySection.displayName = 'CategorySection';

export default CategorySection;
